import { ensureSubscriptionActionAllowed } from '../utils/subscriptionActionGuard';
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Collapse,
  Button,
  Card,
  Descriptions,
  Empty,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  EyeOutlined,
  HistoryOutlined,
  SwapOutlined,
  FileTextOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { useAuth } from "../context/AuthContext";
import { StockPageHeader, StockSummary, StockReadNotice, StockFilters, StockRemoteSelect, StockFilterField,
  useStockWorkspaceTheme, useStockListRefresh, dateParams } from "./StockWorkspace";

const { Text } = Typography;

export const EVENT_OPTIONS = [
  { value: "product_created", label: "Création" },
  { value: "product_updated", label: "Modification" },
  { value: "product_deleted", label: "Suppression" },
  { value: "purchase", label: "Achat" },
  { value: "sales", label: "Vente" },
  { value: "adjustment", label: "Ajustement" },
  { value: "production", label: "Production" },
  { value: "transfer_in", label: "Transfert entrant" },
  { value: "transfer_out", label: "Transfert sortant" },
  { value: "return_in", label: "Retour entrant" },
  { value: "return_out", label: "Retour sortant" },
  { value: "ecommerce_sale", label: "Vente e-commerce" },
  { value: "ecommerce_cancel", label: "Annulation e-commerce" },
  { value: "ecommerce_reactivate", label: "Réactivation e-commerce" },
];

export const EVENT_COLORS = {
  product_created: "green",
  product_updated: "blue",
  product_deleted: "red",
  purchase: "green",
  sales: "volcano",
  adjustment: "gold",
  production: "cyan",
  transfer_in: "geekblue",
  transfer_out: "purple",
  return_in: "lime",
  return_out: "magenta",
  ecommerce_sale: "blue",
  ecommerce_cancel: "red",
  ecommerce_reactivate: "green",
};

const isPlainObject = (value) =>
  Object.prototype.toString.call(value) === "[object Object]";

const formatQuantity = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "-";
  return parsed.toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 5,
  });
};

const formatDetailKey = (key = "") =>
  ({ name: "Nom du produit", item_code: "Code article", barcode_value: "Code-barres" }[key] || key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase()));

const formatDetailValue = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (typeof value === "number") return formatQuantity(value);
  if (typeof value === "string") return value;
  return JSON.stringify(value, null, 2);
};

export const extractBeforeAfterPairs = (details) => {
  if (!isPlainObject(details)) return [];

  const pairs = [];
  const seen = new Set();
  const oldValues = details.old_values || details.oldValues || details.before;
  const newValues = details.new_values || details.newValues || details.after;

  if (isPlainObject(oldValues) || isPlainObject(newValues)) {
    const keys = new Set([
      ...Object.keys(isPlainObject(oldValues) ? oldValues : {}),
      ...Object.keys(isPlainObject(newValues) ? newValues : {}),
    ]);

    keys.forEach((field) => {
      pairs.push({
        key: field,
        field,
        before: oldValues ? oldValues[field] : undefined,
        after: newValues ? newValues[field] : undefined,
      });
      seen.add(field);
    });
  }

  Object.entries(details).forEach(([key, value]) => {
    if (["old_values", "new_values", "oldValues", "newValues", "before", "after"].includes(key)) return;
    if (!key.startsWith("old_") && !key.startsWith("previous_")) return;

    const field = key.replace(/^old_/, "").replace(/^previous_/, "");
    if (!field || seen.has(field)) return;

    pairs.push({
      key: field,
      field,
      before: value,
      after: details[`new_${field}`] ?? details[field],
    });
  });

  return pairs;
};

const extractFlatDetails = (details, maxItems = 30) => {
  if (!isPlainObject(details)) return [];

  return Object.entries(details)
    .filter(
      ([key]) =>
        !["old_values", "new_values", "oldValues", "newValues"].includes(key)
    )
    .slice(0, maxItems)
    .map(([key, value]) => ({
      key,
      label: formatDetailKey(key),
      value,
    }));
};

const StockProductHistory = ({ selectedCompany, selectedWarehouse }) => {
  const workspaceTheme = useStockWorkspaceTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [listError, setListError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [draft, setDraft] = useState({ warehouse: selectedWarehouse || undefined });
  const [filters, setFilters] = useState({ warehouse: selectedWarehouse || undefined });
  const [selectedItem, setSelectedItem] = useState(null);
  const [exporting, setExporting] = useState(false);
  const context = `${user?.id}|${selectedCompany}|${selectedWarehouse}`;
  const [filterContext, setFilterContext] = useState(context);
  const contextRef = useRef(context);
  contextRef.current = context;
  const requestId = useRef(0);
  const buildRequestParams = useCallback((overrides = {}) => ({
    company_id: selectedCompany, page, limit: pageSize,
    search: filters.search?.trim() || undefined, warehouse_id: filters.warehouse || undefined,
    event_types: filters.events?.length ? filters.events.join(",") : undefined,
    actor_id: filters.actor || undefined, ...dateParams(filters.period), ...overrides,
  }), [selectedCompany, page, pageSize, filters]);

  useEffect(() => {
    requestId.current += 1;
    const initial = { warehouse: selectedWarehouse || undefined };
    setDraft(initial); setFilters(initial); setFilterContext(context);
    setPage(1); setItems([]); setTotal(0); setSummary(null); setSelectedItem(null); setListError("");
    return () => { requestId.current += 1; };
  }, [context, selectedWarehouse]);

  const fetchHistory = useCallback(async () => {
    if (!selectedCompany || filterContext !== context) return;
    const id = ++requestId.current;
    const requestedContext = context;
    setLoading(true);
    try {
      const response = await axios.get("/api/product-lifecycle", {
        headers: { "X-Company-Id": String(selectedCompany) }, params: buildRequestParams({ include_summary: 1 }),
      });
      if (id !== requestId.current || requestedContext !== contextRef.current) return;
      setItems(response.data.items || []); setTotal(Number(response.data.total || 0));
      setSummary(response.data.summary || null); setListError("");
    } catch (error) {
      if (id !== requestId.current || requestedContext !== contextRef.current) return;
      setListError("Impossible d’actualiser l’historique des produits.");
    } finally { if (id === requestId.current) setLoading(false); }
  }, [selectedCompany, filterContext, context, buildRequestParams]);
  useEffect(() => { void fetchHistory(); return () => { requestId.current += 1; }; }, [fetchHistory]);
  useStockListRefresh(fetchHistory);
  const resetFilters = () => {
    const initial = { warehouse: selectedWarehouse || undefined };
    setDraft(initial); setFilters(initial); setPage(1);
  };

  const handleExport = async () => {
    if (!(await ensureSubscriptionActionAllowed('data_export'))) return;
    if (!selectedCompany) {
      message.warning("Selectionnez une entreprise avant l'export.");
      return;
    }

    setExporting(true);
    const requestedContext = context;
    try {
      const exportLimit = 100;
      let currentPage = 1;
      let expectedTotal = 0;
      const allItems = [];

      while (true) {
        const response = await axios.get("/api/product-lifecycle", {
          headers: { "X-Company-Id": String(selectedCompany) },
          params: buildRequestParams({
            page: currentPage,
            limit: exportLimit,
            audit_action: currentPage === 1 ? "EXPORT" : undefined,
          }),
        });

        if (requestedContext !== contextRef.current) throw new Error("Le contexte a changé. Relancez l’export.");
        const batch = response.data.items || [];
        expectedTotal = Number(response.data.total || batch.length || 0);
        allItems.push(...batch);

        if (
          batch.length === 0 ||
          batch.length < exportLimit ||
          allItems.length >= expectedTotal
        ) {
          break;
        }

        currentPage += 1;
      }

      if (allItems.length === 0) {
        message.info("Aucune donnee a exporter.");
        return;
      }

      const exportRows = allItems.map((item) => ({
        Date: item.event_date
          ? dayjs(item.event_date).format("YYYY-MM-DD HH:mm:ss")
          : "",
        Produit: item.product_name || "",
        CodeArticle: item.item_code || "",
        Evenement: item.event_label || "",
        Resume: item.summary || "",
        Quantite:
          typeof item.quantity === "number" ? item.quantity : null,
        Unite: item.unit_name || "",
        StockInitial:
          typeof item.opening_stock === "number" ? item.opening_stock : null,
        Établissement: item.warehouse_name || "",
        "Établissement lié": item.related_warehouse_name || "",
        Utilisateur: item.actor_name || "",
        Reference: item.reference_label || "",
        Remarque: item.remarks || "",
        Source: item.source_kind || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const headers = Object.keys(exportRows[0] || {});
      const numericColumns = ["Quantite", "StockInitial"];

      numericColumns.forEach((columnName) => {
        const columnIndex = headers.indexOf(columnName);
        if (columnIndex === -1) return;

        exportRows.forEach((row, rowIndex) => {
          if (typeof row[columnName] !== "number") return;

          const cellRef = XLSX.utils.encode_cell({
            r: rowIndex + 1,
            c: columnIndex,
          });

          worksheet[cellRef] = {
            t: "n",
            v: row[columnName],
            z: "#,##0.00",
          };
        });
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Historique Produits");
      XLSX.writeFile(
        workbook,
        `historique_produits_${dayjs().format("YYYY-MM-DD_HHmmss")}.xlsx`
      );

      message.success("Export Excel genere avec succes.");
    } catch (error) {
      message.error(
        error.response?.data?.message || error.message || "Impossible d'exporter l'historique."
      );
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: "Date et heure",
      dataIndex: "event_date",
      key: "event_date",
      width: 160,
      render: (value) =>
        value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "-",
    },
    {
      title: "Produit",
      key: "product",
      render: (_, record) => (
        <div className="stock-identity">{record.product_name || "-"}</div>
      ),
    },
    {
      title: "Événement",
      key: "event",
      width: 165,
      render: (_, record) => <Tag color={EVENT_COLORS[record.event_type] || "default"}>
        {record.event_label}
      </Tag>,
    },
    {
      title: "Quantité",
      dataIndex: "quantity",
      key: "quantity",
      width: 110,
      render: (value, record) => {
        if (value === null || value === undefined) {
          return "-";
        }


        const prefix = Number(value) > 0 ? "+" : "";
        return (
          <span className={`stock-quantity stock-quantity-${Number(value) >= 0 ? "add" : "subtract"}`}>
            {prefix}
            {formatQuantity(value)} {record.unit_name || ""}
          </span>
        );
      },
    },
    {
      title: "Établissement",
      key: "warehouse",
      width: 130,
      render: (_, record) => (
        <div>
          <div>{record.warehouse_name || "-"}</div>
          {record.related_warehouse_name ? (
            <Text type="secondary">{record.related_warehouse_name}</Text>
          ) : null}
        </div>
      ),
    },
    {
      title: "Utilisateur",
      dataIndex: "actor_name",
      key: "actor_name",
      width: 110,
      render: (value) => value || "-",
    },
    {
      title: "Référence",
      key: "reference",
      width: 170,
      render: (_, record) => record.reference_label || "-",
    },
    {
      title: "",
      key: "actions",
      width: 64,
      align: "center",
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          aria-label={`Voir le détail de l’événement ${record.product_name || "produit"}`}
          title="Voir le détail"
          icon={<EyeOutlined />}
          onClick={() => setSelectedItem(record)}
        />
      ),
    },
  ];

  const beforeAfterPairs = extractBeforeAfterPairs(selectedItem?.details);
  const flatDetails = extractFlatDetails(selectedItem?.details);
  const changedFields = Array.isArray(selectedItem?.details?.changed_fields)
    ? selectedItem.details.changed_fields
    : [];

  if (!selectedCompany) {
    return (
      <Card>
        <Empty description="Selectionnez une entreprise pour consulter l'historique." />
      </Card>
    );
  }

  return (
    <div className="stock-workspace-pane" style={workspaceTheme} data-stock-theme={workspaceTheme["--stock-mode"]}>
      <StockPageHeader description="Retracez la vie de vos produits et les mouvements de stock.">
        <Button type="primary" icon={<FileExcelOutlined />} onClick={handleExport} loading={exporting}>Exporter sur Excel</Button>
      </StockPageHeader>
      <StockSummary loading={loading} items={[
        { label: "Événements", value: summary?.total, icon: <HistoryOutlined /> },
        { label: "Mouvements de stock", value: summary?.movements, icon: <SwapOutlined /> },
        { label: "Événements produit", value: summary?.product_events, icon: <FileTextOutlined /> },
      ]} />
      <StockReadNotice error={listError} retry={fetchHistory} loading={loading} hasData={Boolean(summary || items.length)} />
      <section className="stock-register">
        <div className="stock-register-heading"><div><h2>Historique des produits</h2><p>Consultez les événements et leur origine.</p></div></div>
        <StockFilters searchLabel="Rechercher un produit" placeholder="Nom, code article ou remarque…" value={draft} onChange={setDraft}
          onApply={() => { setFilters(draft); setPage(1); }} onReset={resetFilters}>
          <StockRemoteSelect endpoint="/api/product-lifecycle" params={{}} companyId={selectedCompany} warehouseId={null} kind="warehouses"
            label="Établissement concerné" value={draft.warehouse} placeholder="Tous les établissements"
            onChange={warehouse => setDraft({ ...draft, warehouse })} />
          <StockFilterField label="Événements"><Select aria-label="Événements" mode="multiple" value={draft.events || []} options={EVENT_OPTIONS}
            maxTagCount="responsive" placeholder="Tous les événements" onChange={events => setDraft({ ...draft, events })} /></StockFilterField>
          <StockRemoteSelect endpoint="/api/product-lifecycle" params={{ warehouse_id: draft.warehouse }} companyId={selectedCompany}
            warehouseId={draft.warehouse} kind="actors" label="Utilisateur" value={draft.actor} placeholder="Tous les utilisateurs"
            onChange={actor => setDraft({ ...draft, actor })} />
        </StockFilters>
        <Table className="stock-workspace-table" rowKey="id" loading={loading} columns={columns} dataSource={items} scroll={{ x: 1140 }}
          locale={{ emptyText: listError ? "Données indisponibles." : "Aucun événement pour ces filtres." }}
          pagination={{ current: page, pageSize, total, showSizeChanger: true, pageSizeOptions: ["20", "50", "100"],
            onChange: (nextPage, nextSize) => { setPage(nextPage); setPageSize(nextSize); },
            showTotal: (count, range) => `${range[0]}–${range[1]} sur ${count} événements` }} />
      </section>

      <Modal
        title="Détails de l’événement"
        className="stock-history-details"
        open={!!selectedItem}
        onCancel={() => setSelectedItem(null)}
        footer={null}
        width={960}
      >
        {selectedItem ? (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            {selectedItem.summary && <Typography.Paragraph>{selectedItem.summary}</Typography.Paragraph>}
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Produit">
                {selectedItem.product_name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Code article">
                {selectedItem.item_code || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Événement">
                <Tag color={EVENT_COLORS[selectedItem.event_type] || "default"}>
                  {selectedItem.event_label}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Date">
                {selectedItem.event_date
                  ? dayjs(selectedItem.event_date).format("DD/MM/YYYY HH:mm:ss")
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Établissement">
                {selectedItem.warehouse_name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Utilisateur">
                {selectedItem.actor_name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Quantité">
                {selectedItem.quantity !== null &&
                selectedItem.quantity !== undefined
                  ? `${formatQuantity(selectedItem.quantity)}${
                      selectedItem.unit_name ? ` ${selectedItem.unit_name}` : ""
                    }`
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Référence">
                {selectedItem.reference_label || "-"}
              </Descriptions.Item>
              {selectedItem.related_warehouse_name ? (
                <Descriptions.Item label="Établissement lie" span={2}>
                  {selectedItem.related_warehouse_name}
                </Descriptions.Item>
              ) : null}
              {selectedItem.remarks ? (
                <Descriptions.Item label="Remarque" span={2}>
                  {selectedItem.remarks}
                </Descriptions.Item>
              ) : null}
              {selectedItem.opening_stock !== null &&
              selectedItem.opening_stock !== undefined ? (
                <Descriptions.Item label="Stock initial" span={2}>
                  {formatQuantity(selectedItem.opening_stock)}
                </Descriptions.Item>
              ) : null}
            </Descriptions>

            {changedFields.length > 0 ? (
              <div>
                <div style={{ fontWeight: 500, marginBottom: 8 }}>
                  Champs modifiés
                </div>
                <Space wrap>
                  {changedFields.map((field) => (
                    <Tag key={field}>{formatDetailKey(field)}</Tag>
                  ))}
                </Space>
              </div>
            ) : null}

            {beforeAfterPairs.length > 0 ? (
              <Table
                size="small"
                pagination={false}
                rowKey="key"
                dataSource={beforeAfterPairs}
                columns={[
                  {
                    title: "Champ",
                    dataIndex: "field",
                    key: "field",
                    render: (value) => formatDetailKey(value),
                  },
                  {
                    title: "Avant",
                    dataIndex: "before",
                    key: "before",
                    render: (value) => (
                      <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                        {formatDetailValue(value)}
                      </pre>
                    ),
                  },
                  {
                    title: "Après",
                    dataIndex: "after",
                    key: "after",
                    render: (value) => (
                      <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                        {formatDetailValue(value)}
                      </pre>
                    ),
                  },
                ]}
              />
            ) : null}

            {flatDetails.length > 0 ? (
              <Descriptions bordered size="small" column={1}>
                {flatDetails.map((detail) => (
                  <Descriptions.Item key={detail.key} label={detail.label}>
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                      {formatDetailValue(detail.value)}
                    </pre>
                  </Descriptions.Item>
                ))}
              </Descriptions>
            ) : null}

            {selectedItem.details && <Collapse items={[{ key: "technical", label: "Données techniques", children:
              <pre style={{ margin: 0, maxHeight: 280, overflow: "auto", whiteSpace: "pre-wrap" }}>{JSON.stringify(selectedItem.details, null, 2)}</pre>
            }]} />}

          </Space>
        ) : null}
      </Modal>
    </div>
  );
};

export default StockProductHistory;
